import { useContext, useRef, ReactElement } from "react";
import { useMatch, useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import { ROLE } from "../../shared/constants";
import { ROUTES } from "../../shared/routes";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { SidebarButton } from "./SidebarButton";
import { AppTooltip } from "../common/AppTooltip";
import { SidebarUserMenu } from "../user-menu/SidebarUserMenu";
import PracticesIcon from "../../assets/images/icons/sidebar/fi_star.svg";
import DashboardIcon from "../../assets/images/icons/sidebar/pie-chart.svg";
import TherapistsIcon from "../../assets/images/icons/sidebar/id-badge 1.svg";
import ClientsIcon from "../../assets/images/icons/sidebar/fi_users.svg";
import SettingsIcon from "../../assets/images/icons/sidebar/settings.svg";
import ChevronLeftIcon from "../../assets/images/icons/sidebar/chevrons-left.svg";
import UplifeLogo from "../../assets/images/appbar-uplife-logo.png";
import UplifeLogoFull from "../../assets/images/appbar-uplife-logo-full.webp";
import css from "./sidebar.module.scss";

export function Sidebar() {
  const auth = useContext(AuthContext);
  const role = auth?.role;
  const tenants = auth?.tenants ?? [];
  const ownTenantId = auth?.ownTenantId;
  const ownTenant = auth?.ownTenant;
  const isTenantSuperAdminInTherapistMode =
    auth?.isTenantSuperAdminInTherapistMode ?? false;
  const navigate = useNavigate();
  const location = useLocation();

  const [isCollapsed, setIsCollapsed] = useLocalStorage(
    "sidenavCollapsed",
    false
  );
  const expanded = !isCollapsed;

  const tenantMatch = useMatch("/tenants/:tenantId/*");
  const dashboardMatch = useMatch("/tenants/:tenantId/dashboard");
  const therapistsMatch = useMatch("/tenants/:tenantId/therapists");
  const clientsMatch = useMatch("/tenants/:tenantId/clients");
  const tenantSettingsMatch = useMatch("/tenants/:tenantId/settings/*");

  const lastTenantIdRef = useRef<string | null>(null);
  const matchedTenantId = tenantMatch?.params.tenantId ?? null;
  if (matchedTenantId) {
    lastTenantIdRef.current = matchedTenantId;
  }

  if (!role || role === ROLE.TENANT_USER || role === ROLE.UNKNOWN) return null;

  const isOnRoot = location.pathname === "/";
  const isSuperAdmin = role === ROLE.SUPER_ADMIN;

  const selectedTenantId =
    matchedTenantId ??
    lastTenantIdRef.current ??
    (isSuperAdmin ? null : ownTenantId) ??
    null;
  const selectedTenant =
    selectedTenantId
      ? (tenants.find((t) => t.tenantId === selectedTenantId) ?? null)
      : null;
  const isTenantSuperAdmin = role === ROLE.TENANT_SUPER_ADMIN;
  const showAdminItems =
    isSuperAdmin || (isTenantSuperAdmin && !isTenantSuperAdminInTherapistMode);
  const noTenantSelected = isSuperAdmin && !selectedTenantId;

  const roleBannerLabel = isSuperAdmin
    ? "Super Admin"
    : isTenantSuperAdmin && !isTenantSuperAdminInTherapistMode
    ? "Account Admin"
    : null;

  // For SUPER_ADMIN the top logo is always UpLife — the selected practice is shown
  // as its own nav item below (UP-2957). Tenant users use their own tenant logo.
  const logoSource = isSuperAdmin ? null : ownTenant;
  const ownDisplayName =
    typeof ownTenant?.displayName === "object"
      ? ((ownTenant.displayName as { en?: string })?.en ?? "")
      : ((ownTenant?.displayName as string | undefined) ?? "");
  const firstInitial = ownDisplayName.charAt(0).toUpperCase() || "U";

  const squareLogo = logoSource?.logoSquareUrl ?? null;
  const fullLogo = logoSource?.logoFullUrl ?? null;

  const squareLogoSrc = squareLogo ?? (isSuperAdmin ? UplifeLogo : null);
  const fullLogoSrc =
    fullLogo ?? squareLogo ?? (isSuperAdmin ? UplifeLogoFull : null);

  // Tooltip only on a tenant/practice logo — never on the Super Admin UpLife
  // logo (UP-2959 / UP-3006).
  const withLogoTooltip = (logo: ReactElement) =>
    isSuperAdmin ? (
      logo
    ) : (
      <AppTooltip
        text={ownDisplayName || "Practice"}
        placement="right"
        popperConfig={{ modifiers: [{ name: "offset", options: { offset: [0, 8] } }] }}
      >
        {logo}
      </AppTooltip>
    );

  return (
    <nav
      className={[css.sidebar, expanded ? css.sidebarExpanded : ""]
        .filter(Boolean)
        .join(" ")}
      data-testid="sidebar"
    >
      {/* ── Logo header ─────────────────────────────── */}
      <div
        className={[css.logoRow, expanded ? css.logoRowExpanded : ""]
          .filter(Boolean)
          .join(" ")}
      >
        <div className={css.logoArea}>
          {/* Collapsed: square logo or initial tile — clicking expands the sidebar */}
          {!expanded &&
            withLogoTooltip(
              <button
                className={css.logoExpandBtn}
                onClick={() => setIsCollapsed(false)}
                aria-label="Expand sidebar"
                data-testid="logo-expand-btn"
              >
                {squareLogoSrc ? (
                  <img
                    src={squareLogoSrc}
                    alt={ownDisplayName || "Logo"}
                    className={css.logoSquare}
                    data-testid="logo-square"
                  />
                ) : (
                  <div
                    className={css.logoInitial}
                    aria-label={ownDisplayName}
                    data-testid="logo-initial"
                  >
                    {firstInitial}
                  </div>
                )}
              </button>
            )}
          {/* Expanded: full logo, square fallback, or displayName text */}
          {expanded &&
            (fullLogoSrc
              ? withLogoTooltip(
                  <img
                    src={fullLogoSrc}
                    alt={ownDisplayName || "Logo"}
                    className={css.logoFull}
                    data-testid="logo-full"
                  />
                )
              : withLogoTooltip(
                  <span
                    className={css.logoDisplayName}
                    data-testid="logo-display-name"
                  >
                    {ownDisplayName}
                  </span>
                ))}
        </div>

        {/* Chevron only visible when expanded — clicking collapses the sidebar */}
        {expanded && (
          <button
            className={css.chevronBtn}
            onClick={() => setIsCollapsed(true)}
            aria-label="Collapse sidebar"
            data-testid="sidebar-toggle"
          >
            <img src={ChevronLeftIcon} alt="" width={16} height={16} />
          </button>
        )}
      </div>

      {expanded && roleBannerLabel && (
        <div
          className={[css.roleBanner, isSuperAdmin ? css.roleBannerSuper : css.roleBannerAccount]
            .filter(Boolean)
            .join(" ")}
          data-testid="sidebar-role-banner"
        >
          {roleBannerLabel}
        </div>
      )}
      {!expanded && roleBannerLabel && (
        <div
          className={[css.roleBadgeCollapsed, isSuperAdmin ? css.roleBannerSuper : css.roleBannerAccount]
            .filter(Boolean)
            .join(" ")}
          data-testid="sidebar-role-badge-collapsed"
        >
          ADMIN
        </div>
      )}

      {/* ── Nav items ──────────────────────────────── */}
      {isSuperAdmin && (
        <>
          <SidebarButton
            icon={PracticesIcon}
            label="All Accounts"
            active={isOnRoot}
            onClick={() => navigate(ROUTES.ROOT)}
            isExpanded={expanded}
            data-testid="sidebar-practices"
          />
          <SidebarButton
            icon={selectedTenant?.logoSquareUrl ?? null}
            label={selectedTenant?.displayName ?? "Account"}
            active={false}
            onClick={() => {
              if (!selectedTenantId) return;
              if (isOnRoot) {
                navigate(ROUTES.TENANTS_THERAPISTS(selectedTenantId));
              } else {
                navigate(ROUTES.ROOT);
              }
            }}
            isAvatar
            hasBorder
            isExpanded={expanded}
            data-testid="sidebar-selected-practice"
          />
        </>
      )}
      {showAdminItems && (
        <>
          <SidebarButton
            icon={DashboardIcon}
            label="Dashboard"
            active={!!dashboardMatch}
            onClick={() =>
              selectedTenantId &&
              navigate(ROUTES.TENANTS_DASHBOARD(selectedTenantId))
            }
            isExpanded={expanded}
            disabled={noTenantSelected}
            data-testid="sidebar-dashboard"
          />
          <SidebarButton
            icon={TherapistsIcon}
            label="Therapists"
            active={!!therapistsMatch}
            onClick={() =>
              selectedTenantId &&
              navigate(ROUTES.TENANTS_THERAPISTS(selectedTenantId))
            }
            isExpanded={expanded}
            disabled={noTenantSelected}
            data-testid="sidebar-therapists"
          />
        </>
      )}
      {!isSuperAdmin && (
        <SidebarButton
          icon={ClientsIcon}
          label="Clients"
          active={!!clientsMatch}
          onClick={() => ownTenantId && navigate(ROUTES.TENANTS_CLIENTS(ownTenantId))}
          isExpanded={expanded}
          data-testid="sidebar-clients"
        />
      )}
      <SidebarButton
        icon={SettingsIcon}
        label={showAdminItems ? "Account Settings" : "Settings"}
        active={
          location.pathname.startsWith("/settings") || !!tenantSettingsMatch
        }
        onClick={() => {
          if (role === ROLE.SUPER_ADMIN) {
            if (selectedTenantId) {
              navigate(
                ROUTES.TENANTS_SETTINGS(selectedTenantId) + "/practice"
              );
            }
          } else {
            navigate(showAdminItems ? "/settings/practice" : "/settings/configuration");
          }
        }}
        isExpanded={expanded}
        disabled={noTenantSelected}
        data-testid="sidebar-settings"
      />

      <SidebarUserMenu expanded={expanded} />

      <div className={css.footer} data-testid="sidebar-footer">
        {expanded ? (
          <>
            Powered by{" "}
            <a
              href="https://www.uplifecare.com/"
              target="_blank"
              rel="noopener noreferrer"
              className={css.footerLink}
              data-testid="sidebar-footer-link"
            >
              UpLife Inc
            </a>
          </>
        ) : (
          <AppTooltip
            text="Powered by UpLife Inc"
            placement="right"
            popperConfig={{
              modifiers: [{ name: "offset", options: { offset: [0, 8] } }],
            }}
          >
            <a
              href="https://www.uplifecare.com/"
              target="_blank"
              rel="noopener noreferrer"
              className={css.footerLink}
              data-testid="sidebar-footer-link"
            >
              UpLife
            </a>
          </AppTooltip>
        )}
      </div>
    </nav>
  );
}
